// services/WarehouseProductService.ts
import axios from 'axios';
import { WarehouseProduct } from '../models/WarehouseProduct';

const API_URL = process.env.REACT_APP_SERVER_URL + "/warehouseproduct";
type ImportRow = {
    description: string;
    quantity: number;
    batchNo?: string;
    expiryDate?: string;
    stockGroup?: string;
    batchQuantity?: number;
};

type ImportOptions = { warehouseId?: number; resetFirst?: boolean; skipGroupCode?: boolean };

type ImportResult = { updated: number; inserted: number; batchesUpserted?: number; errors: string[] };

export type ProductSummary = {
    totalProducts: number;
    totalQuantity: number;
    outOfStockCount: number;
    lowStockCount: number;
    byCategory: { categoryName: string; count: number }[];
};

type ImportStatus = {
    jobId: string;
    status: 'running' | 'completed' | 'failed';
    phase?: string;
    processed: number;
    total: number;
    percent: number;
    startedAt: number;
    elapsedMs: number;
    etaMs: number | null;
    result?: ImportResult;
    errors?: string[];
};

export const WarehouseProductService = {

    getAllProducts: async () => {
        const response = await axios.get(`${API_URL}/getAllProducts`);
        return response.data;
    },

    getAllProductsByWarehouseId: async (warehouseId: string) => {
        const response = await axios.get(`${API_URL}/getAllProductsByWarehouseId/${warehouseId}`);
        return response.data;
    },

    getProductsPaginated: async (
        page: number,
        limit: number,
        searchQuery?: string,
        filterStartDate?: string,
        filterEndDate?: string,
        filterYear?: string,
        filterWarehouseId?: string
    ): Promise<{ products: WarehouseProduct[]; total: number }> => {
        const params: Record<string, any> = { page, limit };
        if (searchQuery) params.searchQuery = searchQuery;
        if (filterStartDate) params.filterStartDate = filterStartDate;
        if (filterEndDate) params.filterEndDate = filterEndDate;
        if (filterYear) params.filterYear = filterYear;
        if (filterWarehouseId) params.filterWarehouseId = filterWarehouseId;
        const response = await axios.get(`${API_URL}/getProductsPaginated`, { params });
        return response.data;
    },

    getAllProductsLimit: async (limit: number, offset: number, searchQuery: string, filterStartDate: string, filterEndDate: string, filterYear: string, filterWarehouseId: string) => {
        const response = await axios.get(`${API_URL}/getAllProductsLimit`, {
            params: { limit, offset, searchQuery, filterStartDate, filterEndDate, filterYear, filterWarehouseId },
        });
        return response.data;
    },

    getAllProductsByWarehouseIdLimit: async (warehouseId: string, limit: number, offset: number, searchQuery: string, filterStartDate: string, filterEndDate: string, filterYear: string) => {
        const response = await axios.get(`${API_URL}/getAllProductsByWarehouseIdLimit/${warehouseId}`, {
            params: { limit, offset, searchQuery, filterStartDate, filterEndDate, filterYear },
        });
        return response.data;
    },

    getAllProductsStockRemind: async () => {
        const response = await axios.get(`${API_URL}/getAllProductsStockRemind`);
        return response.data;
    },

    getAllProductsStockRemindByWarehouseId: async (warehouseId: string) => {
        const response = await axios.get(`${API_URL}/getAllProductsStockRemindByWarehouseId/${warehouseId}`);
        return response.data;
    },

    getProductSummary: async (): Promise<ProductSummary> => {
        const response = await axios.get(`${API_URL}/getProductSummary`);
        return response.data;
    },

    getProductSummaryByWarehouseId: async (warehouseId: string): Promise<ProductSummary> => {
        const response = await axios.get(`${API_URL}/getProductSummaryByWarehouseId/${warehouseId}`);
        return response.data;
    },

    getProductById: async (id: number): Promise<WarehouseProduct> => {
        const response = await axios.get(`${API_URL}/getProductById/${id}`);
        return response.data;
    },

    createProduct: async (product: Omit<WarehouseProduct, 'id' | 'created_time' | 'updated_time'>) => {
        const response = await axios.post(`${API_URL}/add`, product);
        return response.data;
    },

    updateProduct: async (id: number, product: Omit<WarehouseProduct, 'created_time' | 'updated_time'>) => {
        const response = await axios.put(`${API_URL}/edit/${id}`, product);
        return response.data;
    },

    updateCurrentQuantity: async (id: number, quantity: number, action: string) => {
        const response = await axios.put(`${API_URL}/updateCurrentQuantity/${id}`, {
            quantity: quantity,
            action: action,
        });
        return response.data;
    },

    deleteProduct: async (id: number): Promise<void> => {
        await axios.delete(`${API_URL}/delete/${id}`);
    },

    startImportQuantities: async (
        rows: ImportRow[],
        options?: ImportOptions
    ): Promise<{ jobId: string }> => {
        const response = await axios.post(`${API_URL}/importQuantities`, {
            rows,
            warehouseId: options?.warehouseId,
            resetFirst: options?.resetFirst,
            skipGroupCode: options?.skipGroupCode,
        });
        return response.data;
    },

    getImportQuantitiesStatus: async (jobId: string): Promise<ImportStatus> => {
        const response = await axios.get(`${API_URL}/importQuantities/status/${jobId}`);
        return response.data;
    },
};
